import { useCallback, useEffect, useState } from 'react';

function readStoredBoolean(key: string, defaultValue: boolean): boolean {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return defaultValue;
    return raw === 'true';
  } catch {
    return defaultValue;
  }
}

export type PersistedBooleanMountCollapse = {
  isNarrow: boolean;
  activeCount: number;
};

/**
 * localStorage に永続化する boolean 状態（詳細フィルター開閉など）。
 * mountCollapse 指定時は「一覧優先で閉じる」を初期値に反映し、effect 待ちの一瞬開きを防ぐ。
 */
export function usePersistedBoolean(
  storageKey: string,
  defaultValue = false,
  mountCollapse?: PersistedBooleanMountCollapse
) {
  const [value, setValueState] = useState(() => {
    if (
      mountCollapse &&
      shouldCollapseDetailedFilterOnMount(mountCollapse.isNarrow, mountCollapse.activeCount)
    ) {
      return false;
    }
    return readStoredBoolean(storageKey, defaultValue);
  });

  const setValue = useCallback(
    (next: boolean | ((prev: boolean) => boolean)) => {
      setValueState((prev) => {
        const resolved = typeof next === 'function' ? next(prev) : next;
        try {
          localStorage.setItem(storageKey, String(resolved));
        } catch {
          /* ignore quota / private mode */
        }
        return resolved;
      });
    },
    [storageKey]
  );

  const toggle = useCallback(() => {
    setValue((prev) => !prev);
  }, [setValue]);

  return [value, setValue, toggle] as const;
}

const NARROW_MQ = '(max-width: 767.98px)';

export function isNarrowViewportNow(): boolean {
  return typeof window !== 'undefined' && window.matchMedia(NARROW_MQ).matches;
}

/** マウント時に詳細フィルターを閉じるべきか（一覧優先） */
export function shouldCollapseDetailedFilterOnMount(
  isNarrow: boolean,
  activeCount: number
): boolean {
  return isNarrow || activeCount === 0;
}

/**
 * 詳細フィルターをマウント時に一覧優先で閉じる。
 * - 狭い画面: 常に閉じる（永続「開いたまま」で表が画面外になるのを防ぐ）
 * - 広い画面: 有効な詳細条件が無いときだけ閉じる
 * 初回（および storageKey 切替）のみ。打鍵中の開閉争奪はしない。
 */
export function useListFirstDetailedFilterOnMount(
  setOpen: (open: boolean) => void,
  activeCount: number,
  resetKey: string
): void {
  useEffect(() => {
    if (shouldCollapseDetailedFilterOnMount(isNarrowViewportNow(), activeCount)) {
      setOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount / key のみ。activeCount は初回スナップショット
  }, [resetKey, setOpen]);
}
